import { Color, websiteColorTheme } from './Color';
import { Z_INDEX_BASE, Z_INDEX_DROPDOWN } from './zIndex';
import { Shadow } from './Shadow';
import { Css, ICustomTheme } from '../ui';
import { FONT_FAMILY_CLEAR_SANS, FONT_FAMILY_DUNBAR_TALL } from './Font';
import { getOutlinedBtnStatesStyles } from './common/getOutlinedBtnStatesStyles';
import { getTextBtnStatesStyles } from './common/getTextBtnStatesStyles';
import { getPrimaryBtnStatesStyles } from './common/getPrimaryBtnStatesStyles';
import { SiteSectionSlug } from '../types/siteSectionSlug';
import { getTagBtnDisabledStateStyles } from './common/getTagBtnDisabledStateStyles';

const BODY_FONTS = `${FONT_FAMILY_CLEAR_SANS}, Arial, Roboto, sans-serif`;
const HEADING_FONTS = `${FONT_FAMILY_DUNBAR_TALL}, Arial, Roboto, sans-serif`;

export const theme: ICustomTheme = {
	layout: {
		main: {
			maxWidth: '1550px',
			textMaxWidth: '576px',
			dialogMaxWidth: '1450px',
			margin: {
				tablet: Css.pxToRem(24),
				mobile: Css.pxToRem(24),
			},
		},
		page: {
			padding: '0',
			background: Color.BodyBackground,
		},
		image: {
			margin: `${Css.pxToRem(24)} 0 ${Css.pxToRem(16)}`,
			maxWidth: '65%',
			mobile: {
				margin: `${Css.pxToRem(16)} 0 ${Css.pxToRem(32)}`,
				maxWidth: '100%',
			},
			tablet: {
				maxWidth: '100%',
			},
		},
	},
	background: {
		default: Color.OnPrimarySurface,
	},
	callToAction: {
		padding: `${Css.pxToRem(32)}`,
		borderRadius: '16px',
		background: {
			default: Color.PrimaryWhite,
			inverted: Color.Tertiary1, // with background image
		},
		font: {
			color: {
				default: Color.PrimaryBlue,
				inverted: Color.PrimaryWhite, // with background image
			},
		},
		mobile: {
			padding: `${Css.pxToRem(32)}`,
		},
	},
	color: {
		highlight: Color.PrimaryBlue,
		brand: Color.PrimaryBlue,
		background: {
			default: Color.OnPrimarySurface,
			light: Color.PrimaryWhite,
		},
		text: {
			default: Color.OnBackgroundText,
			inverted: Color.OnPrimarySurface,
			light: Color.Neutral40,
		},
		border: {
			default: `1px solid ${Color.Neutral80}`,
		},
		icon: {
			default: Color.PrimaryWhite,
			hover: Color.PrimaryBlue,
		},
	},
	grid: {
		gutter: {
			horizontal: {
				default: Css.pxToRem(24),
				mobile: Css.pxToRem(16),
			},
			vertical: {
				default: Css.pxToRem(24),
				mobile: Css.pxToRem(16),
			},
		},
	},
	accordion: {
		title: {
			padding: `${Css.pxToRem(22)} 0 ${Css.pxToRem(16)}`,
		},
		content: {
			padding: `${Css.pxToRem(16)} 0 ${Css.pxToRem(8)}`,
		},
		icon: {
			color: Color.PrimaryBlue,
		},
	},
	typography: {
		body: {
			fontFamily: BODY_FONTS,
			size: '16px',
			lineHeight: '1.5', // Unitless line height (1.5 * 16px = 24px)
			color: Color.OnBackgroundText,
			fontWeight: '400',
		},

		paragraph: {
			margin: '0 0 8px 0',
			fontWeight: '400',
		},

		header: {
			margin: '0 0 16px 0',

			h1: {
				size: Css.pxToRem(96),
				color: Color.OnBackgroundText,
				fontFamily: HEADING_FONTS,
				fontWeight: '700',
			},
			h2: {
				size: Css.pxToRem(64),
				margin: '0 0 8px 0',
				color: Color.OnBackgroundText,
				fontFamily: HEADING_FONTS,
				fontWeight: '700',
			},
			h3: {
				size: Css.pxToRem(52),
				margin: '0 0 8px 0',
				color: Color.OnBackgroundText,
				fontFamily: HEADING_FONTS,
				fontWeight: '500',
			},
			h4: {
				size: Css.pxToRem(32),
				color: Color.OnBackgroundText,
				fontFamily: HEADING_FONTS,
				fontWeight: '500',
			},
			h5: {
				size: Css.pxToRem(24),
				color: Color.OnBackgroundText,
				fontFamily: HEADING_FONTS,
				fontWeight: '500',
			},
			h6: {
				size: Css.pxToRem(20),
				color: Color.OnBackgroundText,
				fontFamily: HEADING_FONTS,
				fontWeight: '500',
			},
			h7: {
				size: Css.pxToRem(16),
				color: Color.OnBackgroundText,
				fontFamily: BODY_FONTS,
				fontWeight: '500',
			},
		},
		mobile: {
			header: {
				h1: {
					size: Css.pxToRem(52),
					weight: '500',
				},
				h2: {
					size: Css.pxToRem(32),
					weight: '500',
				},
				h3: {
					size: Css.pxToRem(32),
					weight: '500',
				},
				h4: {
					size: Css.pxToRem(24),
				},
				h5: {
					size: Css.pxToRem(20),
				},
				h6: {
					size: Css.pxToRem(20),
				},
				h7: {
					size: Css.pxToRem(16),
				},
			},
		},
		link: {
			hover: {
				color: Color.PrimaryBlue,
			},
			color: Color.PrimaryBlue1,
		},
		small: {
			size: Css.pxToRem(14),
			weight: '400',
		},
	},
	card: {
		backgroundColor: 'transparent',
		boxShadow: Shadow.Medium,
		contentMargin: `${Css.pxToRem(24)} ${Css.pxToRem(16)}`,
		contentPadding: `${Css.pxToRem(12)} ${Css.pxToRem(16)} ${Css.pxToRem(16)}`,
		borderRadius: '8px',
		fullyClickable: {
			backgroundColor: Color.PrimaryWhite,
			boxShadow: Shadow.Desktop,
			hover: {
				boxShadow: Shadow.Desktop,
			},
		},
		mobile: {
			boxShadow: 'none',
			hover: {
				boxShadow: 'none',
			},
			fullyClickable: {
				boxShadow: Shadow.Mobile,
				hover: {
					boxShadow: Shadow.Mobile,
				},
			},
		},
		icon: {
			color: Color.PrimaryBlue1,
			backgroundColor: Color.Tertiary3,
		},
		link: {
			color: Color.PrimaryBlue1,
		},
	},
	blockquote: {
		boxShadow: `inset ${Css.pxToRem(4)} 0 0 0 ${Color.PrimaryBlue2}`,
		paragraph: { color: Color.OnBackgroundText, size: Css.pxToRem(16) },
		padding: `0px ${Css.pxToRem(16)}`,
		margin: '0 0 1.5em 0',
		background: 'transparent',
	},
	alertBanner: {
		background: Color.Tertiary1,
		title: {
			fontSize: Css.pxToRem(16),
		},
	},

	button: {
		iconSeparator: Css.pxToRem(8),
		iconSize: Css.pxToRem(24),
		radius: '56px',
		padding: `${Css.pxToRem(12)} ${Css.pxToRem(16)}`,
		primary: {
			background: Color.Tertiary2,
			border: 'none',
			font: {
				color: Color.PrimaryBlue,
				weight: '700',
			},
			...getPrimaryBtnStatesStyles(),
			inverted: {
				border: `none`,
				background: Color.Tertiary1Light,
				font: { color: Color.PrimaryBlue },
				...getPrimaryBtnStatesStyles(),
				hover: {
					background: Color.Tertiary2,
					boxShadow: Shadow.ExtraSmall,
					font: {
						color: Color.PrimaryBlue,
					},
				},
				focus: {
					...getPrimaryBtnStatesStyles().focus,
					background: Color.Tertiary2,
				},
				active: {
					...getPrimaryBtnStatesStyles().active,
					background: Color.Tertiary2,
				},
			},
		},
		outlined: {
			background: 'none',
			border: `1px solid ${Color.Neutral60}`,
			outline: 'none',
			font: {
				color: Color.PrimaryBlue,
				weight: '700',
			},
			...getOutlinedBtnStatesStyles(),
			inverted: {
				border: `1px solid ${Color.Neutral60}`,
				font: { color: Color.PrimaryWhite },
				...getOutlinedBtnStatesStyles(),
			},
		},
		textButton: {
			background: 'none',
			border: 'none',
			font: {
				color: Color.PrimaryBlue1,
				weight: '700',
			},
			...getTextBtnStatesStyles(),
			inverted: {
				border: `none`,
				font: { color: Color.PrimaryWhite, weight: '700' },
				...getTextBtnStatesStyles(),
			},
		},
		secondary: {
			background: Color.PrimaryBlue2,
			border: `none`,
			font: {
				color: Color.PrimaryWhite,
				weight: '700',
			},
			hover: {
				background: Color.SecondaryHovered,
				boxShadow: Shadow.ExtraSmall,
				font: {
					color: Color.PrimaryBlue1,
				},
			},
			focus: {
				background: Color.SecondaryFocused,
				boxShadow: Shadow.ExtraSmall,
				outline: `1px solid ${Color.PrimaryBlue2}`,
				font: {
					color: Color.PrimaryBlue1,
				},
			},
			active: {
				background: Color.SecondaryFocused,
				boxShadow: 'none',
				border: 'none',
				font: {
					color: Color.PrimaryBlue1,
				},
			},
			disabled: {
				background: Color.Neutral10,
				opacity: '1',
				font: {
					color: Color.PrimaryBlue,
				},
			},
		},
		tag: {
			border: `2px solid ${Color.PrimaryBlue1}`,
			background: Color.PrimaryBlue1,
			font: {
				weight: '700',
				color: Color.PrimaryWhite,
			},
			hover: {
				background: Color.PrimaryBlueDark2,
				border: `2px solid ${Color.PrimaryBlueDark2}`,
			},
			focus: {
				background: Color.PrimaryBlue2,
				border: `2px solid ${Color.PrimaryBlue3Light80}`,
			},
			active: {
				background: Color.PrimaryBlue2,
				border: `2px solid ${Color.PrimaryBlue2}`,
			},
			...getTagBtnDisabledStateStyles(),

			inverted: {
				border: `2px solid ${Color.PrimaryBlue1}`,
				background: Color.BodyBackground,
				font: {
					weight: '700',
					color: Color.PrimaryBlue1,
				},
				hover: {
					background: Color.DropdownHighlighted,
					border: `2px solid ${Color.PrimaryBlue1}`,
					font: { color: Color.PrimaryBlue1 },
				},
				focus: {
					background: Color.DropdownHighlighted,
					border: `2px solid ${Color.PrimaryBlue3}`,
					font: { color: Color.PrimaryBlue1 },
				},
				active: {
					background: Color.DropdownHighlighted,
					border: `2px solid ${Color.DropdownHighlighted}`,
					font: { color: Color.PrimaryBlue1 },
				},
				...getTagBtnDisabledStateStyles(),
			},
		},
	},
	zIndex: {
		base: `${Z_INDEX_BASE}`,
		dropdown: `${Z_INDEX_DROPDOWN}`,
	},
	select: {
		main: {
			background: Color.Neutral94,
			color: Color.OnBackgroundText,
			borderRadius: '100px',
			boxShadow: '0px 2px 0px 0px #00000014',
			borderBottom: `none`,
			padding: '14px',
		},

		focus: {
			boxShadow: 'none',
			borderBottom: `none`,
			borderRadius: '100px',
		},
	},
	input: {
		placeholder: {
			color: Color.PrimaryBlue,
			focussed: {
				color: Color.PrimaryBlue,
			},
		},
	},
	dropdownList: {
		option: {
			active: {
				background: Color.SecondaryFocused,
			},
			selected: {
				fontWeight: '700',
				background: 'unset',
			},
			'highlighted-selected': {
				color: Color.PrimaryBlue,
				background: Color.DropdownHighlighted,
				fontWeight: '700',
			},
			highlighted: {
				color: Color.PrimaryBlue,
				background: Color.DropdownHighlighted,
				fontWeight: 'unset',
			},
		},
	},
	border: {
		default: `1px solid ${Color.Neutral80}`,
	},
	shimmer: {
		placeholder: {
			borderRadius: '4px',
			background: Color.OnPrimarySurface,
		},
	},
	image: {
		borderRadius: {
			default: '8px',
			mobileXL: '8px',
		},
	},
	imageCollection: {
		borderRadius: '8px',
		gap: Css.pxToRem(24),
		aspectRatio: {
			columns1: '1424/480',
			columns2: '700/360',
			columns4: '338/320',
		},
		mobile: {
			gap: Css.pxToRem(16),
			aspectRatio: {
				columns1: '345/220',
				columns2: '165/180',
			},
		},
	},
	highlight: {
		image: {
			boxShadow: Shadow.Desktop,
		},
		mobile: {
			image: {
				boxShadow: 'none',
			},
		},
	},
};

export const generateTheme = (slug: SiteSectionSlug) => {
	const colorTheme = slug && websiteColorTheme[slug];
	const { secondary, secondaryLight, secondaryDark } = colorTheme || {};

	return {
		...theme,
		color: {
			...theme.color,
			brand: secondaryDark,
			background: {
				default: secondary,
				light: secondaryLight,
			},
		},
	};
};
